import { followOnLettersByLunarMansions, locales } from './Locales.js';
import { calculateAyanamsa, d2r, longitudeToZodiac, toJulianDate, } from './Utils.js';
import { getLongitudeOfMoonVakkiya } from './Vakya.js';
function getLongitudeOfMoonThirukanitha(date) {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();
    const hour = date.getHours() + date.getMinutes() / 60;
    const timezoneOffset = date.getTimezoneOffset() / 60;
    const julianDate = toJulianDate(day, month, year);
    const t = (julianDate -
        /* Year 2000 in Julian */ 2451545 +
        /* Adjust hours for UTC */ (hour + timezoneOffset) / 24 -
        0.5) /
        36525;
    const ayanamsa = calculateAyanamsa(t);
    // Calculate Moon longitude, latitude, and distance using truncated Chapront algorithm
    // Mean longitude, referred to the mean equinox of the date
    let longitude = 218.3164591 + 481267.88134236 * t;
    // Mean elongation of the moon
    const d = (297.8502042 + 445267.1115168 * t) * d2r;
    // Sun's mean anomaly
    const m = (357.5291092 + 35999.0502909 * t) * d2r;
    // Moon's mean anomaly
    const mm = (134.9634114 + 477198.8676313 * t) * d2r;
    // Moon's argument of latitude
    const f = (93.2720993 + 483202.0175273 * t) * d2r;
    const e = 1 - 0.002516 * t - 0.0000074 * t * t;
    const p = 6.288774 * Math.sin(mm) +
        1.274027 * Math.sin(d * 2 - mm) +
        0.658314 * Math.sin(d * 2) +
        0.213618 * Math.sin(2 * mm) -
        0.185116 * e * Math.sin(m) -
        0.114332 * Math.sin(f * 2) +
        (0.058793 * Math.sin(d * 2 - mm * 2) +
            0.057066 * e * Math.sin(d * 2 - m - mm) +
            0.053322 * Math.sin(d * 2 + mm) +
            0.045758 * e * Math.sin(d * 2 - m) -
            0.040923 * e * Math.sin(m - mm) -
            0.03472 * Math.sin(d) -
            0.030383 * e * Math.sin(m + mm)) +
        (0.015327 * Math.sin(d * 2 - f * 2) -
            0.012528 * Math.sin(mm + f * 2) +
            0.01098 * Math.sin(mm - f * 2) +
            0.010675 * Math.sin(d * 4 - mm) +
            0.010034 * Math.sin(3 * mm)) +
        (0.008548 * Math.sin(d * 4 - mm * 2) -
            0.007888 * e * Math.sin(d * 2 + m - mm) -
            0.006766 * e * Math.sin(d * 2 + m) -
            0.005163 * Math.sin(d - mm) +
            0.004987 * e * Math.sin(d + m) +
            0.004036 * e * Math.sin(d * 2 - m + mm) +
            0.003994 * Math.sin(d * 2 + mm * 2));
    longitude += p;
    // Keep within 360 degrees
    while (longitude < 0.0) {
        longitude += 360.0;
    }
    while (longitude > 360.0) {
        longitude -= 360.0;
    }
    longitude += ayanamsa;
    if (longitude < 0.0)
        longitude += 360.0;
    return longitude;
}
export const DEFAULT_PANJANGAM = 'thirukanitha';
/**
 * Both the moon sign and the lunar mansion are read off this one longitude, so
 * a method is only ever a different way of computing it. Methods are registered
 * as they are implemented; `getStateFromParams` only accepts the ones listed,
 * so an unimplemented method cannot reach the calculation.
 */
const moonLongitudeByPanjangam = {
    thirukanitha: getLongitudeOfMoonThirukanitha,
    vakkiya: getLongitudeOfMoonVakkiya,
};
export const implementedPanjangams = Object.keys(moonLongitudeByPanjangam);
export const isImplementedPanjangam = (value) => implementedPanjangams.includes(value);
export function getLongitudeOfMoon(date, panjangam = DEFAULT_PANJANGAM) {
    const calculate = moonLongitudeByPanjangam[panjangam] || getLongitudeOfMoonThirukanitha;
    return calculate(date);
}
export function getMoonSignIndex(date, panjangam) {
    return longitudeToZodiac(getLongitudeOfMoon(date, panjangam));
}
export function getLunarMansionIndex(date, panjangam) {
    return Math.floor((getLongitudeOfMoon(date, panjangam) * 60) / 800.0);
}
/**
 * Arcminutes in one padham. A lunar mansion spans 13deg20', a quarter of which
 * is 3deg20' - 200'. The quarter matters because it is what decides the
 * syllable a name should start with: the mansion narrows it to four, and the
 * padham picks one of them.
 */
const PADHAM = 200;
/**
 * Which quarter of its mansion the moon stood in, 0-3.
 *
 * Read off the same longitude as the mansion itself rather than derived from
 * it, so the two can never disagree about where a boundary is: a mansion ends
 * exactly where its fourth padham does.
 */
export function getPadhamIndex(date, panjangam) {
    return Math.floor((getLongitudeOfMoon(date, panjangam) * 60) / PADHAM) % 4;
}
export function getMoonSign(dateOrIndex, locale) {
    return locales[locale].moonSigns[dateOrIndex instanceof Date
        ? getMoonSignIndex(dateOrIndex)
        : dateOrIndex];
}
export function getLunarMansion(dateOrIndex, locale) {
    return locales[locale].lunarMansions[dateOrIndex instanceof Date
        ? getLunarMansionIndex(dateOrIndex)
        : dateOrIndex];
}
export function getPadhamLettersForName(dateOrIndex, locale) {
    return locales[locale].namingLettersByPadham[dateOrIndex instanceof Date
        ? getLunarMansionIndex(dateOrIndex)
        : dateOrIndex];
}
export function getStartingLettersForName(dateOrIndex, locale) {
    return locales[locale].namingLettersByLunarMansions[dateOrIndex instanceof Date
        ? getLunarMansionIndex(dateOrIndex)
        : dateOrIndex];
}
export function getFollowOnLettersForName(dateOrIndex) {
    const index = dateOrIndex instanceof Date
        ? getLunarMansionIndex(dateOrIndex)
        : dateOrIndex;
    const own = new Set(locales.ta.namingLettersByLunarMansions[index]);
    return followOnLettersByLunarMansions[index].filter((letter) => !own.has(letter));
}
//# sourceMappingURL=Signs.js.map